import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { apiClient } from "../utils/const";
import { toast } from "react-toastify";
import { useTheme } from "./ThemeContext";

const EditWithdrawal = ({ withdrawalId, onClose, onSuccess }) => {
  const [schemeType, setSchemeType] = useState(localStorage.getItem("schemeType"));
  const [withdrawal, setWithdrawal] = useState(null);
  const [amount, setAmount] = useState("");
  const [withdrawalType, setWithdrawalType] = useState("");
  const [withdrawalDate, setWithdrawalDate] = useState("");
  const [goldGms, setGoldGms] = useState(""); // for kanchan
  const { dark } = useTheme();

  const normalizeWithdrawalType = (value) => {
    if (!value) return "";
    const v = String(value).trim().toLowerCase();
    if (v === "cash") return "Cash";
    if (v === "upi") return "UPI";
    if (v === "cheque" || v === "bank transfer" || v === "banktransfer" || v === "bank_transfer")
      return "Bank Transfer";
    if (v === "net banking" || v === "netbanking") return "Net Banking";
    return value;
  };

  // Listen for scheme changes
  useEffect(() => {
    const handleSchemeChange = (event) => {
      const newScheme = event.detail.to;
      setSchemeType(newScheme);
    };

    window.addEventListener("schemeChanged", handleSchemeChange);
    return () => window.removeEventListener("schemeChanged", handleSchemeChange);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const endpoint =
          schemeType === "kanchan"
            ? "/kanchan/fetch_kanchan_payments.php"
            : "/mandal/fetch_mandal_payments.php";

        const res = await apiClient.post(endpoint);
        const withdrawals = res.data?.data?.transactions || [];

        const foundWithdrawal = withdrawals.find(
          (w) => String(w.id) === String(withdrawalId)
        );

        if (!foundWithdrawal) {
          toast.error("❌ Withdrawal not found");
          onClose();
          return;
        }

        setWithdrawal(foundWithdrawal);
        setAmount(foundWithdrawal.withdrawal_amount || foundWithdrawal.amount || "");
        setWithdrawalDate(foundWithdrawal.withdrawal_date || foundWithdrawal.transaction_date || "");

        if (schemeType === "kanchan") {
          setWithdrawalType("Metal"); // fixed
          setGoldGms(foundWithdrawal.withdrawal_in_gms || "");
        } else {
          setWithdrawalType(
            normalizeWithdrawalType(
              foundWithdrawal.withdrawal_type || foundWithdrawal.transaction_method || ""
            )
          );
        }
      } catch (error) {
        console.error("Error loading withdrawal", error);
        toast.error("❌ Failed to load data");
        onClose();
      }
    };

    fetchData();
  }, [withdrawalId, schemeType, onClose]);

  const getFullName = (user) => {
    if (user?.full_name) return user.full_name; // from new APIs
    return [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(" ");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawal) return;

    const payload = {
      withdrawal_id: withdrawalId,
      withdrawal_amount: parseFloat(amount),
      withdrawal_type: schemeType === "kanchan" ? "Metal" : normalizeWithdrawalType(withdrawalType),
      withdrawal_date: withdrawalDate,
    };

    if (schemeType === "kanchan") {
      payload.withdrawal_in_gms = parseFloat(goldGms) || 0;
    }

    try {
      const updateEndpoint =
        schemeType === "kanchan"
          ? "/kanchan/update_kanchan_withdrawal.php"
          : "/mandal/update_mandal_withdrawal.php";

      const res = await apiClient.post(updateEndpoint, payload);

      if (res.data.message === "radha") {
        toast.success("Withdrawal updated successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error("❌ Failed to update withdrawal: " + (res.data.message || ""));
      }
    } catch (err) {
      console.error(err);
      toast.error("❌ Error while updating withdrawal");
    }
  };

  const selectStyles = useMemo(
    () => ({
      control: (provided) => ({
        ...provided,
        minHeight: 44,
        backgroundColor: dark ? "rgba(255,255,255,0.06)" : "#f8f9fa",
        borderColor: dark ? "rgba(255,255,255,0.1)" : "var(--bs-border-color)",
        boxShadow: "none",
        color: dark ? "#f1f1f1" : "#212529",
        borderRadius: 12,
        transition: "all 0.2s ease",
        opacity: 0.9,
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: "6px 12px",
      }),
      singleValue: (provided) => ({
        ...provided,
        color: dark ? "#e9ecef" : "#495057",
        fontWeight: 500,
      }),
      placeholder: (provided) => ({
        ...provided,
        color: dark ? "rgba(255,255,255,0.5)" : "#6c757d",
      }),
      input: (provided) => ({
        ...provided,
        color: dark ? "#f8f9fa" : "#212529",
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        display: "none",
      }),
      indicatorSeparator: () => ({
        display: "none",
      }),
      menu: (provided) => ({
        ...provided,
        display: "none",
      }),
    }),
    [dark]
  );

  if (!withdrawal) return <div className="p-4">Loading withdrawal data...</div>;

  return (
    <form onSubmit={handleSubmit} autoComplete="off">
      <div className="row px-2">
        <div className="col-md-8 mb-2">
          <label className="form-label">Member</label>
          <Select
            value={{
              label: `Card ID: ${withdrawal.user_id}, Name: ${getFullName(withdrawal)}, Phone: ${withdrawal.phone || "-"}`,
              value: withdrawal.user_id,
            }}
            isDisabled
            styles={selectStyles}
            className="react-select-member"
            classNamePrefix="react-select"
          />
        </div>
      </div>

      <div className="row px-2">
        {schemeType === "kanchan" && (
          <div className="col-md-4 mb-2">
            <label className="form-label">Total Gold (gms)</label>
            <input
              type="number"
              className="form-control"
              value={goldGms}
              onChange={(e) => setGoldGms(e.target.value)}
              min="0"
              step="0.01"
              required
            />
          </div>
        )}

        <div className="col-md-4 mb-2">
          <label className="form-label">Amount (₹)</label>
          <input
            type="number"
            className="form-control"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="0"
            step="0.01"
            required
          />
        </div>

        <div className="col-md-4 mb-2">
          <label className="form-label">Withdrawal Type</label>
          {schemeType === "kanchan" ? (
            <input type="text" className="form-control" value="Metal" readOnly disabled />
          ) : (
            <select
              className="form-select"
              value={withdrawalType}
              onChange={(e) => setWithdrawalType(e.target.value)}
              required
            >
              <option value="">-- Select --</option>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Net Banking">Net Banking</option>
            </select>
          )}
        </div>

        <div className="col-md-4 mb-2">
          <label className="form-label">Withdrawal Date</label>
          <input
            type="date"
            className="form-control"
            value={withdrawalDate}
            onChange={(e) => setWithdrawalDate(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4 px-2">
        <button type="submit" className="btn update-btn">
          Update Withdrawal
        </button>
      </div>

      <style>{`
        .update-btn {
          background-color: #4B2C3B;
          color: #fff;
          border: none;
        }
        .update-btn:hover {
          background-color: rgba(211,175,55,0.8);
        }
      `}</style>
    </form>
  );
};

export default EditWithdrawal;
